import React, { useState, useEffect, useRef } from 'react'
import { isIOS } from 'react-device-detect'

import './Celluar.css'
import { Images } from 'components/images/Images'
import { FrameWithButton } from 'components/frame/FrameWithButton'
import { VideoBackground } from 'components/background/VideoBackground'
import { bar as initialBar } from 'images/frame/ui'
import { images as initialImages } from 'images/collections'
import { backgroundVideos as videos } from 'images/frame/background'
import { getElementById, playBackgroundVideo, preventMobileScroll, isIOSAndPWA } from 'utils'
import { BarType, ImageType, BackgroundVideoName as backName } from 'types'
import { Config } from 'configs'

const IMAGES_ID = 'images'
const BAR_ID = 'bar'

const FIRST = 0
const LAST = initialImages.length - 1

const INTERVAL_MS = 40
const WEIGHT_CLICK = 0.05
const WEIGHT_WHEEL = 0.1
const WEIGHT_TOUCH = 3

enum SLIDE_OPERATION {
  PREV = 0,
  NEXT = 1,
}

enum Event {
  CLICK,
  WHEEL,
  TOUCH,
}

const isSlideOperationNext = (ope: SLIDE_OPERATION) => {
  return ope === SLIDE_OPERATION.NEXT
}

// スライド中にresizeがあった場合の調整処理
const adjustImages = (topId: string, bottomId: string) => {
  const imagesHeight = getElementById(IMAGES_ID).clientHeight
  let topHeight = getElementById(topId).clientHeight
  let bottomHeight = getElementById(bottomId).clientHeight

  if (imagesHeight - topHeight - bottomHeight < -1) {
    const p = (topHeight + bottomHeight - imagesHeight) / 2
    topHeight -= p
    bottomHeight -= p
  } else if (imagesHeight - topHeight - bottomHeight > 1) {
    const p = (imagesHeight - (topHeight + bottomHeight)) / 2
    topHeight += p
    bottomHeight += p
  }

  return { topHeight, bottomHeight }
}

export const Celluar = () => {
  const [top, setTop] = useState(0)
  const [bottom, setBottom] = useState(top + 1)
  const [images, setImages] = useState<ImageType[]>(initialImages)
  const [bar, setBar] = useState<BarType>(initialBar)
  const [backName, setBackName] = useState<backName>(initialImages[0].backName)
  const [prevOperation, setPrevOperation] = useState<SLIDE_OPERATION>(SLIDE_OPERATION.NEXT)
  const [isSliding, setIsSliding] = useState(false)
  const [isWheeling, setIsWheeling] = useState(false)
  const [isTouching, setIsTouching] = useState(false)
  const [intervalID, setIntervalID] = useState(0)
  const [clearInterval, setClearInterval] = useState(false)
  const [touchStart, setTouchStart] = useState(0)

  // for inside eventListener
  const imagesRef = useRef<ImageType[] | null>(null)
  const isSlidingRef = useRef<boolean | null>(null)
  const prevOperationRef = useRef<SLIDE_OPERATION | null>(null)
  const barRef = useRef<BarType | null>(null)
  const topRef = useRef<number | null>(null)
  const bottomRef = useRef<number | null>(null)
  imagesRef.current = images
  isSlidingRef.current = isSliding
  prevOperationRef.current = prevOperation
  barRef.current = bar
  topRef.current = top
  bottomRef.current = bottom

  useEffect(() => {
    let unmounted = false

    ;(async () => {
      if (!unmounted) {
        window.addEventListener('touchmove', preventMobileScroll)

        isIOSAndPWA()
          ? window.addEventListener('orientationchange', adjustBarAndImages)
          : window.addEventListener('resize', adjustBarAndImages)
      }
    })()

    return () => {
      unmounted = true
      window.removeEventListener('touchmove', preventMobileScroll)

      isIOSAndPWA()
        ? window.removeEventListener('orientationchange', adjustBarAndImages)
        : window.removeEventListener('resize', adjustBarAndImages)
    }
  }, [])

  useEffect(() => {
    let unmounted = false

    ;(async () => {
      if (clearInterval && !unmounted) {
        window.clearInterval(intervalID)
        setClearInterval(false)
      }
    })()

    return () => {
      unmounted = true
    }
  }, [clearInterval])

  const adjustBarAndImages = () => {
    const bar = getElementById(BAR_ID)
    if (bar.style.display === 'block' && !isSlidingRef.current!) {
      const currentImages = [...imagesRef.current!]

      const imagesHeight = getElementById(IMAGES_ID).clientHeight
      const { topHeight, bottomHeight } = adjustImages(
        currentImages[topRef.current!].id,
        currentImages[bottomRef.current!].id
      )

      moveBar(
        isSlideOperationNext(prevOperationRef.current!) ? bottomHeight : topHeight,
        imagesHeight,
        prevOperationRef.current!
      )

      currentImages[topRef.current!].style = {
        ...currentImages[topRef.current!].style,
        height: Math.round(topHeight),
      }
      currentImages[bottomRef.current!].style = {
        ...currentImages[bottomRef.current!].style,
        height: Math.round(bottomHeight),
      }

      setImages(currentImages)
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isSliding) return

    const operation: string = e.currentTarget.id
    if (operation === 'next') {
      setIsSliding(true)
      setIntervalID(
        window.setInterval(() => {
          slideImage(SLIDE_OPERATION.NEXT, Event.CLICK)
        }, INTERVAL_MS)
      )
    } else if (operation === 'prev') {
      setIsSliding(true)
      setIntervalID(
        window.setInterval(() => {
          slideImage(SLIDE_OPERATION.PREV, Event.CLICK)
        }, INTERVAL_MS)
      )
    } else if (operation === 'top') {
      goBackToFirstImage()
    } else if (operation === 'store') {
      window.open(Config.storeURL, '_blank')
    }
  }

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (isSliding || isTouching) return

    const wheelDirection = e.deltaY
    const operation = wheelDirection < 0 ? SLIDE_OPERATION.PREV : SLIDE_OPERATION.NEXT

    setIsWheeling(true)
    slideImage(operation, Event.WHEEL)
  }

  const handleTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isIOS) playBackgroundVideo()

    if (isSliding || isWheeling) return

    if (e.type === 'touchstart') {
      setTouchStart(e.touches[0].pageY)
    } else if (e.type === 'touchmove') {
      const currentTouchEnd = e.touches[0].pageY
      const slideTouchDistance = touchStart - currentTouchEnd

      setIsTouching(true)
      if (slideTouchDistance < 0) {
        slideImage(SLIDE_OPERATION.NEXT, Event.TOUCH, slideTouchDistance)
      } else {
        slideImage(SLIDE_OPERATION.PREV, Event.TOUCH, slideTouchDistance)
      }
      setTouchStart(currentTouchEnd)
    }
  }

  const slideImage = (operation: SLIDE_OPERATION, event: Event, slideTouchDistance?: number) => {
    const imagesHeight = getElementById(IMAGES_ID).clientHeight
    let slidePixel = 0

    if (event === Event.CLICK) {
      slidePixel = imagesHeight * WEIGHT_CLICK
    } else if (event === Event.WHEEL) {
      slidePixel = imagesHeight * WEIGHT_WHEEL
    } else if (event === Event.TOUCH) {
      slidePixel = slideTouchDistance ? slideTouchDistance * WEIGHT_TOUCH : 0
    }
    slidePixel = Math.abs(Math.round(slidePixel))

    let currentTop = top
    let currentBottom = bottom

    // next->prevといった処理の際に、topとdownの位置をずらす
    if (
      operation !== prevOperation &&
      !isWheeling &&
      !isTouching &&
      top !== FIRST &&
      bottom !== LAST
    ) {
      if (operation) {
        currentTop = bottom
        currentBottom = bottom + 1
      } else {
        currentBottom = top
        currentTop = top - 1
      }

      setTop(currentTop)
      setBottom(currentBottom)
    }

    const currentImages = [...images]
    let { topHeight, bottomHeight } = adjustImages(images[currentTop].id, images[currentBottom].id)

    if (operation) {
      topHeight -= slidePixel
      bottomHeight += slidePixel

      if (topHeight < 0) topHeight = 0

      currentImages[currentTop].style = {
        ...currentImages[currentTop].style,
        height: Math.round(topHeight),
        backgroundPosition: 'top',
      }
      currentImages[currentBottom].style = {
        ...currentImages[currentBottom].style,
        height: Math.round(bottomHeight),
        backgroundPosition: 'bottom',
      }

      moveBar(bottomHeight, imagesHeight, operation)

      if (bottomHeight >= imagesHeight) {
        currentImages[currentTop].style = {
          ...currentImages[currentTop].style,
          height: 0,
          top: 0,
        }
        currentImages[currentBottom].style = {
          ...currentImages[currentBottom].style,
          height: 'auto',
          top: 0,
          bottom: '',
          backgroundPosition: 'top',
        }

        if (currentBottom !== LAST) {
          setTop(currentBottom)
          setBottom(currentBottom + 1)
        }

        changeBackground(currentImages[currentBottom].backName)
        setClearInterval(true)
        setIsSliding(false)
        setIsWheeling(false)
        setIsTouching(false)
      }
    } else {
      topHeight += slidePixel
      bottomHeight -= slidePixel

      if (bottomHeight < 0) bottomHeight = 0

      currentImages[currentTop].style = {
        ...currentImages[currentTop].style,
        height: Math.round(topHeight),
      }
      currentImages[currentBottom].style = {
        ...currentImages[currentBottom].style,
        height: Math.round(bottomHeight),
        top: '',
        bottom: 0,
        backgroundPosition: 'bottom',
      }

      moveBar(topHeight, imagesHeight, operation)

      if (topHeight >= imagesHeight) {
        currentImages[currentTop].style = {
          ...currentImages[currentTop].style,
          height: 'auto',
        }
        currentImages[currentBottom].style = {
          ...currentImages[currentBottom].style,
          height: 0,
          top: '',
          bottom: 0,
          backgroundPosition: 'bottom',
        }

        if (currentTop !== FIRST) {
          setBottom(currentTop)
          setTop(currentTop - 1)
        }

        changeBackground(currentImages[currentTop].backName)
        setClearInterval(true)
        setIsSliding(false)
        setIsWheeling(false)
        setIsTouching(false)
      }
    }

    setImages(currentImages)
    setPrevOperation(operation)
  }

  const moveBar = (barTop: number, imagesHeight: number, operation: SLIDE_OPERATION) => {
    const currentBar = { ...barRef.current! }
    if (barTop <= 0 || barTop >= imagesHeight) {
      currentBar.style = {
        ...currentBar.style,
        display: 'none',
      }
    } else {
      currentBar.style = {
        ...currentBar.style,
        top: isSlideOperationNext(operation) ? '' : Math.round(barTop),
        bottom: isSlideOperationNext(operation) ? Math.round(barTop) : '',
        display: 'block',
      }
    }
    setBar(currentBar)
  }

  const goBackToFirstImage = () => {
    const currentImages = [...images]
    const currentBar = { ...bar }

    for (let i = 1; i <= bottom; i++) {
      currentImages[i].style = {
        ...currentImages[i].style,
        height: 0,
        top: '',
        bottom: 0,
        backgroundPosition: 'bottom',
      }
    }
    currentImages[0].style = {
      ...currentImages[0].style,
      height: 'auto',
      top: 0,
      bottom: '',
      backgroundPosition: 'top',
    }
    currentBar.style = {
      ...currentBar.style,
      display: 'none',
    }

    setPrevOperation(SLIDE_OPERATION.NEXT)
    setTop(FIRST)
    setBottom(FIRST + 1)
    setIsSliding(false)
    setIsWheeling(false)
    setIsTouching(false)
    setImages(currentImages)
    setBar(currentBar)
    setBackName(currentImages[0].backName)
  }

  const changeBackground = (backName: backName) => {
    setBackName(backName)
  }

  return (
    <div className="main">
      <FrameWithButton
        barStyle={bar.style}
        handleWheel={handleWheel}
        handleTouch={handleTouch}
        handleClick={handleClick}
      >
        <VideoBackground videos={videos} currentBack={backName}>
          <Images images={images} currentNum={bottom} />
        </VideoBackground>
      </FrameWithButton>
    </div>
  )
}
